// Design tokens — ported from the wireframe's shared.jsx.
// Three palettes, all sharing the warm-gold accent; vary base ink/paper temperature.
// Picked at runtime via the AppContext's `paletteName`.

export type PaletteName = 'navyCream' | 'inkPaper' | 'signal';
export type ViewMode = 'ledger' | 'field';
export type SidebarMode = 'labels' | 'icons' | 'collapsed';
export type Role = 'admin' | 'editor' | 'viewer' | 'volunteer';

export interface Palette {
  name: string;
  ink: string;
  inkSoft: string;
  paper: string;
  paperWarm: string;
  line: string;
  slate: string;
  muted: string;
  gold: string;
  goldDeep: string;
  leaf: string;
  clay: string;
  swatch: readonly [string, string, string];
}

export const PALETTES: Record<PaletteName, Palette> = {
  navyCream: {
    name: 'Navy & Cream',
    ink: '#1a1f2e',
    inkSoft: '#2a3041',
    paper: '#f5f3ee',
    paperWarm: '#ebe7dd',
    line: '#d8d2c4',
    slate: '#7a8599',
    muted: '#a8a59d',
    gold: '#c4a875',
    goldDeep: '#a08552',
    leaf: '#5a6e58',
    clay: '#c47a5a',
    swatch: ['#1a1f2e', '#f5f3ee', '#c4a875'],
  },
  inkPaper: {
    name: 'Ink & Clay',
    ink: '#1c1c1c',
    inkSoft: '#2f2c28',
    paper: '#faf7f2',
    paperWarm: '#efe9dd',
    line: '#dcd5c5',
    slate: '#76746e',
    muted: '#b8b3a8',
    gold: '#c4a875',
    goldDeep: '#9a7f4a',
    leaf: '#5a6e58',
    clay: '#c8553d',
    swatch: ['#1c1c1c', '#faf7f2', '#c8553d'],
  },
  signal: {
    name: 'Signal',
    ink: '#171a1d',
    inkSoft: '#28302f',
    paper: '#f1ede4',
    paperWarm: '#dfd9cc',
    line: '#cfc8b8',
    slate: '#6a6862',
    muted: '#a39e94',
    gold: '#d4a23c',
    goldDeep: '#a07a20',
    leaf: '#4e7a5a',
    clay: '#b85c38',
    swatch: ['#171a1d', '#f1ede4', '#d4a23c'],
  },
};

// Font stacks. The HTML preconnects to fonts.googleapis.com and pulls these.
export const FONTS = {
  serif: "'Instrument Serif', 'Newsreader', Georgia, serif",
  serifText: "'Newsreader', 'Instrument Serif', Georgia, serif",
  sans: "'Geist', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

// Activity tag definitions — used by ActivityChip and the volunteers/lists screens.
export const ACTIVITIES = [
  { key: 'canvass', label: 'Canvass', icon: '▲' },
  { key: 'phone', label: 'Phone bank', icon: '●' },
  { key: 'poll', label: 'Poll greet', icon: '◆' },
  { key: 'social', label: 'Socials', icon: '✦' },
  { key: 'data', label: 'Data entry', icon: '■' },
] as const;

export type ActivityKey = (typeof ACTIVITIES)[number]['key'];

// Variant: the wireframe explored two design directions per screen.
// Production exposes both via a per-user toggle.
export const VIEW_MODES: ReadonlyArray<{ value: ViewMode; label: string; description: string }> = [
  { value: 'ledger', label: 'Ledger', description: 'Editorial · paper · organizer’s notebook' },
  { value: 'field', label: 'Field', description: 'Dark · dense · ops command-deck' },
];

export const SIDEBAR_MODES: ReadonlyArray<{ value: SidebarMode; label: string }> = [
  { value: 'labels', label: 'Labels' },
  { value: 'icons', label: 'Icons' },
  { value: 'collapsed', label: 'Hidden' },
];
